import { useCallback, useMemo, useState } from "react";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import {
  bulletsToSqafActionItems,
  bulletsToSqafInsightCards,
  bulletsToSqafRowField,
} from "@/utils/mistralInsightMappers";
import { buildSqafExecutiveMarkdown } from "@/utils/sqaafMistralInsights";
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  MapPin,
  RefreshCw,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";
import { fmt2 } from "@/lib/sqaafLabels";
import { toTitleCase } from "@/utils/toTitleCase";
import {
  buildBlockBarLayout,
  buildBlockComparisonData,
  buildDonutGradient,
  buildExecutiveActionSignal,
  buildExecutiveHeroBody,
  buildGaugeStyle,
  buildHistogramBars,
  buildHistogramYTicks,
  buildInsightCards,
  buildLowestQuestionsTable,
  buildPolicyActions,
  buildWatchlistRows,
  getDistrictBandCopy,
  getDistrictBandLabel,
} from "@/utils/sqaafExecutiveDerived";

function SvgIcon({ children, className = "svg-icon" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      {children}
    </svg>
  );
}

export default function SqafExecutiveOverview({
  overview,
  blockPerf,
  intervention,
  showSkeleton,
  refreshing,
  onRefresh,
  onExport,
  onNavigateTab,
  lowestQuestions = [],
  tableBandFilter = "Critical",
  onTableBandFilterChange,
  tableBlockFilter = "",
  onTableBlockFilterChange,
  blockOptions = [],
}) {
  const [localBand, setLocalBand] = useState(tableBandFilter);
  const [localBlock, setLocalBlock] = useState(tableBlockFilter || "All");

  const districtMean = overview?.district_mean ?? 0;
  const districtBand = getDistrictBandLabel(districtMean);
  const gaugeStyle = buildGaugeStyle(districtMean);
  const criticalCount =
    overview?.band_counts?.Critical ??
    intervention?.intervention_summary?.critical_count ??
    0;

  const hasData = Boolean(overview?.n_schools);
  const buildLocalMarkdown = useCallback(
    () => buildSqafExecutiveMarkdown({ overview, blockPerf, intervention, lowestQuestions }),
    [overview, blockPerf, intervention, lowestQuestions]
  );
  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        n: overview?.n_schools,
        m: overview?.district_mean,
        c: criticalCount,
        q: lowestQuestions?.length,
      }),
    [overview?.n_schools, overview?.district_mean, criticalCount, lowestQuestions?.length]
  );
  const {
    loading: aiLoading,
    pickFromBucket,
    pickHeadline,
    pickPrimaryAction,
    pickMapped,
    isMistral,
  } = useMistralNarrativeOverlay({
    kind: "sqaaf-executive",
    title: "SQAAF Executive Overview",
    buildLocalMarkdown,
    context: { overview, blockPerf, intervention, lowestQuestions },
    enabled: hasData,
    scopeKey,
  });

  const bandDistribution = overview?.band_distribution || [];
  const donutGradient = buildDonutGradient(bandDistribution);
  const localInsightCards = useMemo(
    () => buildInsightCards(overview, criticalCount),
    [overview, criticalCount]
  );
  const insightCards = useMemo(
    () => (aiLoading ? localInsightCards : pickMapped(localInsightCards, "insights", bulletsToSqafInsightCards)),
    [aiLoading, pickMapped, localInsightCards]
  );
  const localWatchlist = useMemo(
    () => buildWatchlistRows(overview, blockPerf),
    [overview, blockPerf]
  );
  const watchlist = useMemo(
    () =>
      aiLoading
        ? localWatchlist
        : pickMapped(localWatchlist, "root", (bullets, rows) => bulletsToSqafRowField(bullets, rows, "note")) ||
          localWatchlist,
    [aiLoading, pickMapped, localWatchlist]
  );
  const localPolicyActions = useMemo(
    () => buildPolicyActions(overview, criticalCount, intervention),
    [overview, criticalCount, intervention]
  );
  const policyActions = useMemo(
    () =>
      aiLoading
        ? localPolicyActions
        : pickMapped(localPolicyActions, "actions", bulletsToSqafActionItems) || localPolicyActions,
    [aiLoading, pickMapped, localPolicyActions]
  );
  const localHeroBody = useMemo(() => buildExecutiveHeroBody(overview), [overview]);
  const heroBody = useMemo(
    () => (aiLoading ? localHeroBody : pickFromBucket(localHeroBody, "insights", 0) || localHeroBody),
    [aiLoading, pickFromBucket, localHeroBody]
  );
  const localHeadline = useMemo(() => getDistrictBandCopy(districtBand), [districtBand]);
  const headline = useMemo(
    () => (aiLoading ? localHeadline : pickHeadline(localHeadline)),
    [aiLoading, pickHeadline, localHeadline]
  );
  const localActionSignal = useMemo(
    () => buildExecutiveActionSignal(overview, blockPerf, intervention),
    [overview, blockPerf, intervention]
  );
  const actionSignal = useMemo(
    () =>
      aiLoading
        ? localActionSignal
        : pickFromBucket(localActionSignal, "root", 0) || pickPrimaryAction(localActionSignal),
    [aiLoading, pickFromBucket, pickPrimaryAction, localActionSignal]
  );
  const statusChips = useMemo(() => {
    const chips = [
      { label: `${districtBand} Band`, className: "" },
      { label: `${Number(criticalCount).toLocaleString()} Critical Schools`, className: "red" },
      { label: `${fmt2(overview?.completion_rate)}% Completion`, className: "blue" },
    ];
    if (isMistral && !aiLoading) {
      return [{ label: "Mistral AI Assisted", className: "purple" }, ...chips];
    }
    return chips;
  }, [districtBand, criticalCount, overview?.completion_rate, isMistral, aiLoading]);

  const blockBars = buildBlockComparisonData(overview, blockPerf);
  const histogramBars = buildHistogramBars(overview?.score_histogram || []);
  const histogramYTicks = buildHistogramYTicks(overview?.score_histogram || []);
  const blockBarLayout = buildBlockBarLayout(blockBars);
  const localQuestionRows = useMemo(() => buildLowestQuestionsTable(lowestQuestions), [lowestQuestions]);
  const questionRows = useMemo(
    () =>
      aiLoading
        ? localQuestionRows
        : pickMapped(localQuestionRows, "recs", (bullets, rows) => bulletsToSqafRowField(bullets, rows, "action")) ||
          localQuestionRows,
    [aiLoading, pickMapped, localQuestionRows]
  );

  const bandList = useMemo(() => {
    const order = ["Critical", "Developing", "Strong", "Exemplary", "Needs Focus"];
    const dotClass = {
      Critical: "red",
      Developing: "yellow",
      Strong: "green",
      Exemplary: "blue",
      "Needs Focus": "orange",
    };
    return order.map((band) => {
      const item = bandDistribution.find((b) => b.band === band);
      return { band, pct: fmt2(item?.pct ?? 0), dot: dotClass[band] };
    });
  }, [bandDistribution]);

  const handleBandFilter = (v) => {
    setLocalBand(v);
    onTableBandFilterChange?.(v);
  };

  const handleBlockFilter = (v) => {
    setLocalBlock(v);
    onTableBlockFilterChange?.(v === "All" ? "" : v);
  };

  if (showSkeleton && !overview) {
    return <div className="sq-exec-skeleton" data-testid="sqaf-executive-skeleton" />;
  }

  return (
    <>
      <section className="page-header" id="overview">
        <div>
          <div className="eyebrow">
            <SvgIcon>
              <path d="M3 3v18h18" />
              <path d="m19 9-5 5-4-4-3 3" />
            </SvgIcon>
            Executive summary · Pune District · 2025–26
          </div>
          <h1>Executive Overview</h1>
          <p>
            One glance view of district SQAAF quality, coverage, inequality and policy action priorities with clear
            drill-down paths for blocks, domains and schools.
          </p>
        </div>
        <div className="header-actions">
          <button type="button" className="ghost-action" onClick={onExport}>
            <SvgIcon>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </SvgIcon>
            Export
          </button>
          <button type="button" className="primary-action" onClick={onRefresh} disabled={refreshing}>
            <RefreshCw className={`svg-icon ${refreshing ? "animate-spin" : ""}`} strokeWidth={2} />
            Refresh
          </button>
        </div>
      </section>

      <section className="hero-summary">
        <div className="hero-layout">
          <div>
            <div className="score-focus">
              <div className="gauge" style={gaugeStyle}>
                <div>
                  <strong>{fmt2(districtMean)}%</strong>
                  <span>SQAAF Score</span>
                </div>
              </div>
              <div className="score-copy">
                <h2 className="dashboard-kpi-hero-title">{toTitleCase(headline)}</h2>
                <p>{heroBody}</p>
                <div className="status-row">
                  {statusChips.map((chip) => (
                    <span key={chip.label} className={`status-chip ${chip.className}`.trim()}>
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="summary-note">
              <div className="icon-box red">
                <AlertTriangle className="svg-icon" strokeWidth={2} />
              </div>
              <div>
                <b>Executive action signal</b>
                <span>{actionSignal}</span>
              </div>
            </div>
          </div>
          <div className="metric-grid">
            <article className="metric-card">
              <div className="metric-head">
                <small>Median Score</small>
                <span className="mini-badge purple">
                  <BarChart3 className="svg-icon" strokeWidth={2} />
                </span>
              </div>
              <strong>{fmt2(overview?.district_median)}%</strong>
              <span>Typical school score</span>
            </article>
            <article className="metric-card">
              <div className="metric-head">
                <small>Intervention Load</small>
                <span className="mini-badge red">
                  <AlertTriangle className="svg-icon" strokeWidth={2} />
                </span>
              </div>
              <strong>{fmt2(overview?.intervention_load)}%</strong>
              <span>Schools below 60%</span>
            </article>
            <article className="metric-card">
              <div className="metric-head">
                <small>Full Compliance</small>
                <span className="mini-badge green">
                  <Target className="svg-icon" strokeWidth={2} />
                </span>
              </div>
              <strong>{fmt2(overview?.full_compliance_rate)}%</strong>
              <span>
                {Number(overview?.full_compliance_count || 0).toLocaleString()} schools answered{" "}
                {overview?.n_questions || 128}/{overview?.n_questions || 128}
              </span>
            </article>
            <article className="metric-card">
              <div className="metric-head">
                <small>Std Dev</small>
                <span className="mini-badge orange">
                  <TrendingUp className="svg-icon" strokeWidth={2} />
                </span>
              </div>
              <strong>{fmt2(overview?.district_std)}</strong>
              <span>Performance spread</span>
            </article>
            <article className="metric-card">
              <div className="metric-head">
                <small>Equity Gap</small>
                <span className="mini-badge orange">
                  <Users className="svg-icon" strokeWidth={2} />
                </span>
              </div>
              <strong>{fmt2(overview?.equity_gap)}</strong>
              <span>Top 25% minus bottom 25%</span>
            </article>
            <article className="metric-card">
              <div className="metric-head">
                <small>Inter-block Gap</small>
                <span className="mini-badge purple">
                  <MapPin className="svg-icon" strokeWidth={2} />
                </span>
              </div>
              <strong>{fmt2(overview?.block_gap_display)}</strong>
              <span>Best minus worst block mean</span>
            </article>
          </div>
        </div>
      </section>

      <div className="section-title" id="domains">
        <div>
          <h2>Quality Distribution and Band Mix</h2>
          <p>
            Charts are grouped by decision flow: first understand spread, then identify the school bands creating the
            intervention workload.
          </p>
        </div>
        <button type="button" className="section-action" onClick={() => onNavigateTab?.("intervention")}>
          View policy actions →
        </button>
      </div>

      <section className="analytics-grid">
        <article className="panel">
          <div className="panel-head">
            <div>
              <h3>SQAAF Score Distribution</h3>
              <p>Histogram of school scores in 5% bands. Most schools cluster between 55% and 75%.</p>
            </div>
            <span className="pill">5% bins</span>
          </div>
          <div className="chart-surface">
            <svg viewBox="0 0 640 300" role="img" aria-label="SQAAF score histogram">
              <line className="axis" x1="50" y1="250" x2="610" y2="250" />
              <line className="axis" x1="50" y1="20" x2="50" y2="250" />
              {histogramYTicks.map((tick) => (
                <g key={tick.y}>
                  <line className="gridline" x1="50" y1={tick.y} x2="610" y2={tick.y} />
                  <text className="axis-text" x={20} y={tick.y + 4}>
                    {tick.value}
                  </text>
                </g>
              ))}
              <g>
                {histogramBars.map((bar, i) => (
                  <rect key={i} className="bar-blue" x={bar.x} y={bar.y} width={bar.width} height={bar.height} rx="3" />
                ))}
              </g>
              {[0, 20, 40, 60, 80, 100].map((tick, i) => (
                <text key={tick} className="axis-text" x={47 + i * 103} y={274}>
                  {tick}
                </text>
              ))}
              <text className="axis-text" x={300} y={294}>
                Score %
              </text>
            </svg>
          </div>
        </article>

        <article className="panel">
          <div className="panel-head">
            <div>
              <h3>Score Band Distribution</h3>
              <p>Share of schools by quality band. The developing band is the largest intervention opportunity.</p>
            </div>
            <span className="pill">Quality bands</span>
          </div>
          <div className="chart-surface">
            <div className="donut-layout">
              <div className="donut-wrap">
                <div className="donut" style={{ background: donutGradient }}>
                  <div className="donut-center">
                    <div>
                      <strong>{Number(overview?.n_schools || 0).toLocaleString()}</strong>
                      <span>Schools</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="band-list">
                {bandList.map((row) => (
                  <div key={row.band} className="band-row">
                    <span className="band-name">
                      <i className={`dot ${row.dot}`} />
                      {row.band}
                    </span>
                    <strong>{row.pct}%</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </article>
      </section>

      <section className="insight-grid">
        {insightCards.map((card) => (
          <article key={card.title} className="insight-card">
            <div className={`icon-box ${card.tone}`}>
              {card.tone === "red" && <AlertTriangle className="svg-icon" strokeWidth={2} />}
              {card.tone === "orange" && <Users className="svg-icon" strokeWidth={2} />}
              {card.tone === "green" && <CheckCircle2 className="svg-icon" strokeWidth={2} />}
            </div>
            <h3>{toTitleCase(card.title)}</h3>
            <p>{card.body}</p>
          </article>
        ))}
      </section>

      <div className="section-title" id="blocks">
        <div>
          <h2>Block Performance and Gap View</h2>
          <p>
            Top and bottom blocks are placed together so leaders can quickly identify mentoring, support and escalation
            candidates.
          </p>
        </div>
        <button type="button" className="section-action" onClick={() => onNavigateTab?.("schools")}>
          Open diagnostics →
        </button>
      </div>

      <section className="analytics-grid">
        <article className="panel">
          <div className="panel-head">
            <div>
              <h3>Top 5 vs Bottom 5 Blocks</h3>
              <p>Block mean score comparison for targeted intervention planning.</p>
            </div>
            <span className="pill">Mean score</span>
          </div>
          <div className="chart-surface">
            <svg viewBox="0 0 660 300" role="img" aria-label="Top 5 and bottom 5 blocks chart">
              <line className="axis" x1="60" y1="250" x2="620" y2="250" />
              <line className="axis" x1="60" y1="30" x2="60" y2="250" />
              {[195, 140, 85].map((y) => (
                <line key={y} className="gridline" x1="60" y1={y} x2="620" y2={y} />
              ))}
              {[0, 20, 40, 60, 80].map((tick, i) => (
                <text key={tick} className="axis-text" x={34} y={254 - i * 55}>
                  {tick}
                </text>
              ))}
              {blockBarLayout.map((bar) => (
                <g key={`${bar.block}-${bar.x}`}>
                  <rect
                    className={bar.tone === "green" ? "bar-green" : "bar-red"}
                    x={bar.x}
                    y={bar.y}
                    width={bar.width}
                    height={bar.height}
                    rx="7"
                  />
                  <text
                    className={bar.tone === "green" ? "label-green" : "label-red"}
                    x={bar.x + bar.width / 2}
                    y={bar.y - 8}
                    textAnchor="middle"
                  >
                    {bar.score}
                  </text>
                  <text
                    className="axis-text"
                    x={bar.x + bar.width / 2}
                    y={276}
                    textAnchor="middle"
                    transform={`rotate(-28 ${bar.x + bar.width / 2} 276)`}
                  >
                    {bar.block}
                  </text>
                </g>
              ))}
            </svg>
          </div>
        </article>

        <article className="panel">
          <div className="panel-head">
            <div>
              <h3>Inter-Block Performance Gap</h3>
              <p>Difference between highest and lowest block mean scores.</p>
            </div>
            <span className="pill">Gap signal</span>
          </div>
          <div className="chart-surface">
            <div className="gap-panel">
              <div className="gap-value">{fmt2(overview?.block_gap_display)}</div>
              <div className="gap-label">points between best and worst block mean</div>
              <div className="gap-bar" />
              <div className="gap-legend">
                <span>Best block</span>
                <span>Gap</span>
                <span>Worst block</span>
              </div>
            </div>
          </div>
        </article>
      </section>

      <section className="two-column" id="actions">
        <article className="panel">
          <div className="panel-head">
            <div>
              <h3>Block Watchlist</h3>
              <p>Use this list for review meeting prioritization.</p>
            </div>
            <span className="pill">10 blocks</span>
          </div>
          <div className="block-list">
            {watchlist.map((row) => (
              <div key={row.block} className={`block-row ${row.kind === "low" ? "low" : ""}`}>
                <span className="rank">{row.rank}</span>
                <div>
                  <b>{row.block}</b>
                  <span>{row.note}</span>
                </div>
                <div className={`block-score ${row.kind === "top" ? "score-good" : ""}`}>{fmt2(row.score)}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="panel">
          <div className="panel-head">
            <div>
              <h3>Policy Action Queue</h3>
              <p>Focused actions that convert the dashboard into district improvement work.</p>
            </div>
            <span className="pill">Owner view</span>
          </div>
          <div className="action-stack">
            {policyActions.map((action) => (
              <div key={action.title} className="action-item">
                <div className={`icon-box ${action.tone}`}>
                  {action.tone === "red" && <AlertTriangle className="svg-icon" strokeWidth={2} />}
                  {action.tone === "orange" && <BarChart3 className="svg-icon" strokeWidth={2} />}
                  {action.tone === "blue" && <Target className="svg-icon" strokeWidth={2} />}
                </div>
                <div>
                  <h4>{action.title}</h4>
                  <p>{action.body}</p>
                </div>
                <span className={`priority ${action.priorityClass}`}>{action.priority}</span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="panel table-card" id="diagnostics">
        <div className="table-head">
          <div>
            <h3>Lowest Response Questions</h3>
            <p>Prioritize questions that repeatedly receive zero or low scores across critical schools.</p>
          </div>
          <div className="table-tools">
            <label className="filter-chip">
              Band{" "}
              <b>
                <select
                  className="sq-inline-select"
                  value={localBand}
                  onChange={(e) => handleBandFilter(e.target.value)}
                  aria-label="Filter questions by band"
                >
                  {["Critical", "Needs Focus", "Developing", "All"].map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </b>
            </label>
            <label className="filter-chip">
              Block{" "}
              <b>
                <select
                  className="sq-inline-select"
                  value={localBlock}
                  onChange={(e) => handleBlockFilter(e.target.value)}
                  aria-label="Filter questions by block"
                >
                  <option value="All">All</option>
                  {(blockOptions || []).map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </b>
            </label>
          </div>
        </div>
        <div className="table-wrap">
          <table aria-label="Lowest response questions">
            <thead>
              <tr>
                <th>Section</th>
                <th>Question Theme</th>
                <th>Avg Score /4</th>
                <th>Score %</th>
                <th>Suggested Action</th>
                <th>Priority</th>
              </tr>
            </thead>
            <tbody>
              {questionRows.length ? (
                questionRows.map((row, i) => (
                  <tr key={i}>
                    <td>{row.section}</td>
                    <td>{row.theme}</td>
                    <td>{row.avgOf4}</td>
                    <td className={row.isLow ? "score-bad" : ""}>{row.scorePct}%</td>
                    <td>{row.action}</td>
                    <td>
                      <span className={`tag ${row.priorityTag}`}>{row.priority}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>No low-scoring questions for the current filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
